// frontend/src/pages/PipelineHistory.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from '../components/Modal';
import {
  ArrowLeft, Clock, Database, Calendar, CheckCircle2,
  XCircle, Activity, Filter, ArrowUpDown, ChevronLeft, ChevronRight,
  Play, Pause, AlertTriangle, RefreshCw, Download, Zap, Code2,
  ShieldCheck
} from 'lucide-react';
import { apiClient } from '../api/client';
import { useLanguageStore } from '../store/useLanguageStore'; // 🟢 1. Importamos i18n
import { formatCompact, formatTime, formatUnixDate, translateCron, formatErrorMessage } from '../utils/formatters';

export const PipelineHistory = ({ pipeline, historyLogs, onBack, onRefresh, isRefreshing }) => {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const { t } = useLanguageStore(); // 🟢 2. Instanciamos el traductor

  const [downloadingId, setDownloadingId] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false, title: '', message: '', showCancel: false, onAccept: null
  });

  const getStatusBadge = (status) => {
    if (status === 1) return <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-bold"><Play className="w-3 h-3 mr-1" /> {t('dashboard.status.active') || 'Activo'}</span>;
    if (status === 2) return <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-bold"><Pause className="w-3 h-3 mr-1" /> {t('dashboard.status.paused') || 'Pausado'}</span>;
    return <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-md bg-slate-200 text-slate-600 text-xs font-bold"><AlertTriangle className="w-3 h-3 mr-1" /> {t('dashboard.status.annulled') || 'Anulado'}</span>;
  };

  const handleDownload = async (uuid) => {
    setDownloadingId(uuid);
    try {
      const response = await apiClient.get(`/onboarding/exports/download/${uuid}`);
      window.location.href = response.data.download_url;
    } catch (err) {
      console.error("Error al descargar:", err);
      setModalState({
        isOpen: true,
        title: t('common.error_title') || 'Error',
        message: t('dashboard.history.errors.download_failed') ||
          'No se pudo descargar el archivo. Es posible que haya expirado.',
        showCancel: false,
        onAccept: () => setModalState(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setDownloadingId(null);
    }
  };

  // 🔍 S2 Sync Check: helper de verificación
  const getSyncCheckInfo = (log) => {
    const src = log.sourceRowCount;
    const tgt = log.targetRowCount;

    // No calculado (motor no soportado, error, o pre-Sprint 12.2)
    if (src == null && tgt == null) return null;

    // Solo source (target flat file)
    if (src != null && tgt == null) {
      return { icon: 'info', color: 'slate', src, tgt: null, label: t('dashboard.history.sync.source_only') || 'Solo origen verificado' };
    }

    // Ambos disponibles — comparar
    if (src != null && tgt != null) {
      const diff = Math.abs(tgt - src);
      const pct = src > 0 ? (diff / src) * 100 : (tgt === 0 ? 0 : 100);

      if (diff === 0) {
        return { icon: 'match', color: 'emerald', src, tgt, pct: 0, label: t('dashboard.history.sync.match') || 'Verificado: todos los registros llegaron' };
      }
      if (pct <= 5) {
        return { icon: 'warn', color: 'amber', src, tgt, pct: pct.toFixed(1), diff, label: t('dashboard.history.sync.minor') || 'Discrepancia menor' };
      }
      return { icon: 'error', color: 'red', src, tgt, pct: pct.toFixed(1), diff, label: t('dashboard.history.sync.major') || 'Discrepancia mayor' };
    }

    return null;
  };

  // BUG FIX: setCurrentPage era efecto secundario dentro de useMemo (anti-pattern React).
  // Ahora el reset de página está en su propio useEffect.
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sort]);

  const processedLogs = useMemo(() => {
    let result = [...(historyLogs || [])];
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

    // BUG FIX: log.createdAt puede no existir en LogMigrateResult.
    // Usamos dateStartUnixMicro (microsegundos → /1000 → ms) como fuente de tiempo.
    const getLogTimeMs = (log) => {
      if (log.dateStartUnixMicro) return log.dateStartUnixMicro / 1000;
      if (log.createdAt) return log.createdAt * 1000;
      return now;
    };

    if (filter === 'errors') {
      result = result.filter(log => log.errors > 0);
    } else if (filter === '7d') {
      result = result.filter(log => (now - getLogTimeMs(log)) <= sevenDaysMs);
    } else if (filter === 'prev_7d') {
      result = result.filter(log => {
        const diff = now - getLogTimeMs(log);
        return diff > sevenDaysMs && diff <= sevenDaysMs * 2;
      });
    }

    result.sort((a, b) => {
      const volA = a.success + a.errors, volB = b.success + b.errors;
      const timeA = a.dateStartUnixMicro || (a.createdAt * 1000000) || 0;
      const timeB = b.dateStartUnixMicro || (b.createdAt * 1000000) || 0;
      switch (sort) {
        case 'date_desc': return timeB - timeA;
        case 'date_asc': return timeA - timeB;
        case 'err_desc': return b.errors - a.errors;
        case 'err_asc': return a.errors - b.errors;
        case 'vol_desc': return volB - volA;
        case 'vol_asc': return volA - volB;
        case 'time_desc': return b.timeSec - a.timeSec;
        case 'time_asc': return a.timeSec - b.timeSec;
        default: return 0;
      }
    });

    return result;
  }, [historyLogs, filter, sort]);

  const totalPages = Math.ceil(processedLogs.length / itemsPerPage);
  const currentLogs = processedLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const kpis = useMemo(() => {
    let totalSuccess = 0, totalErrors = 0, totalTime = 0;
    const now = Date.now();
    const logs7d = (historyLogs || []).filter(log => (now - (log.createdAt * 1000)) <= (7 * 24 * 60 * 60 * 1000));
    logs7d.forEach(log => { totalSuccess += log.success; totalErrors += log.errors; totalTime += log.timeSec; });
    const totalVol = totalSuccess + totalErrors;
    const rate = totalVol > 0 ? ((totalSuccess / totalVol) * 100).toFixed(1) : 0;
    const eff = totalTime > 0 ? Math.round(totalVol / totalTime) : 0;
    return { totalSuccess, totalErrors, rate, eff };
  }, [historyLogs]);

  return (
    <div className="w-full max-w-7xl mx-auto pb-12 animate-in fade-in">

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div className="flex items-start">
            <button onClick={onBack} className="p-2 mr-3 sm:mr-4 rounded-full bg-slate-100 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition shrink-0 mt-1">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center mb-1">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 truncate mr-2">{pipeline?.name}</h2>
                <span className="text-xs font-mono bg-slate-100 text-slate-500 px-2 py-1 rounded-md">ID: {pipeline?.idConfigHead}</span>
                {getStatusBadge(pipeline?.status)}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6 text-xs text-slate-600 mt-3">
                <div className="flex items-start"><Calendar className="w-4 h-4 mr-1.5 shrink-0 text-slate-400" /> <span>{t('dashboard.history.created') || 'Creado:'} {formatUnixDate(pipeline?.createdAt ? pipeline.createdAt * 1000 : null)}</span></div>

                <div className="flex items-start"><Database className="w-4 h-4 mr-1.5 shrink-0 text-indigo-400" /> <span className="truncate max-w-[200px]" title={pipeline?.sourceTable}><b>{t('dashboard.history.source_short') || 'Or:'}</b> {pipeline?.sourceType} ({pipeline?.sourceTable})</span></div>
                <div className="flex items-start"><Database className="w-4 h-4 mr-1.5 shrink-0 text-indigo-400" /> <span className="truncate max-w-[200px]" title={pipeline?.targetTable}><b>{t('dashboard.history.target_short') || 'De:'}</b> {pipeline?.targetType} ({pipeline?.targetTable})</span></div>
                <div className="flex flex-col sm:col-span-2 lg:col-span-3">
                  <span className="flex items-start cursor-help font-bold text-slate-700" title={`CRON: ${pipeline?.cron || 'N/A'}`}>
                    <Clock className="w-4 h-4 mr-1.5 shrink-0 text-slate-400" />
                    {translateCron(pipeline?.cron, pipeline?.frequency)}
                  </span>
                  {pipeline?.startDate && (
                    <span className="text-[11px] text-slate-500 ml-5 mt-1 block font-medium">
                      {t('dashboard.history.validity') || 'Vigencia:'} {t('dashboard.history.from') || 'Del'} {pipeline.startDate} {t('dashboard.history.to') || 'al'} {pipeline.endDate || t('dashboard.history.no_limit')}
                    </span>
                  )}
                  {pipeline?.timeWindow && (
                    <span className="text-[11px] text-slate-500 ml-5 mt-0.5 block font-medium">
                      {t('dashboard.history.hours_from') || 'Horas: De'} {pipeline.timeWindow}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex sm:flex-row flex-col bg-slate-50 border border-slate-100 rounded-xl p-3 sm:gap-6 gap-3 shrink-0">
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">{t('dashboard.history.processed_7d') || 'Procesado (7D)'}</p><p className="text-lg font-black text-slate-700">{formatCompact(kpis.totalSuccess + kpis.totalErrors)}</p></div>
            <div className="hidden sm:block w-px bg-slate-200"></div>
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">{t('dashboard.history.effectiveness_7d') || 'Efectividad (7D)'}</p><p className={`text-lg font-black ${kpis.totalErrors > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{kpis.rate}%</p></div>
            <div className="hidden sm:block w-px bg-slate-200"></div>
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">{t('dashboard.history.performance_7d') || 'Rendimiento (7D)'}</p><p className="text-lg font-black text-blue-600">{kpis.eff.toLocaleString('en-US')} <span className="text-xs font-normal text-blue-500/70">r/s</span></p></div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4 w-full gap-1 sm:gap-2">
        <div className="text-[11px] sm:text-sm font-bold text-slate-700 whitespace-nowrap bg-white px-2 sm:px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm shrink-0">
          <span className="hidden sm:inline">{t('dashboard.history.total') || 'Total:'} </span>
          {processedLogs.length > 9999 ? '>9,999' : processedLogs.length.toLocaleString('en-US')}
          <span className="hidden sm:inline"> {t('dashboard.history.logs') || 'logs'}</span>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2 shrink-0">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 ml-1.5 shrink-0 hidden sm:block" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent text-[10px] sm:text-xs font-semibold pl-1.5 pr-4 sm:pr-6 py-1.5 outline-none cursor-pointer text-slate-700">
              <option value="date_desc">{t('dashboard.history.sort.recent') || 'Recientes'}</option>
              <option value="date_asc">{t('dashboard.history.sort.oldest') || 'Antiguos'}</option>
              <option value="err_desc">{t('dashboard.history.sort.errors') || 'Errores(May-Men)'}</option>
              <option value="vol_desc">{t('dashboard.history.sort.volume') || 'Volumen(May-Men)'}</option>
              <option value="time_desc">{t('dashboard.history.sort.time') || 'Tiempo(May-Men)'}</option>
            </select>
          </div>

          <div className="flex items-center bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <Filter className="w-3.5 h-3.5 text-slate-400 ml-1.5 shrink-0 hidden sm:block" />
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-transparent text-[10px] sm:text-xs font-semibold pl-1.5 pr-4 sm:pr-6 py-1.5 outline-none cursor-pointer text-slate-700">
              <option value="all">{t('dashboard.history.filter.all') || 'Todos'}</option>
              <option value="errors">{t('dashboard.history.filter.errors') || 'Con errores'}</option>
              <option value="7d">{t('dashboard.history.filter.last_7d') || 'Últ. 7D'}</option>
              <option value="prev_7d">{t('dashboard.history.filter.prev_7d') || 'Semana anterior'}</option>
            </select>
          </div>

          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-1.5 bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg shadow-sm transition disabled:opacity-50 shrink-0"
            title={t('dashboard.history.refresh') || 'Refrescar historial'}
          >
            <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[700px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[12%]">{t('dashboard.history.table.detail') || 'Detalle'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[18%]">{t('dashboard.history.table.dates') || 'Fechas'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[13%]">{t('dashboard.history.table.volume') || 'Volumen'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[10%]">{t('dashboard.history.table.verification') || 'Verif.'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-emerald-500 uppercase tracking-wider w-[7%] text-center">{t('data_rules.history_quality')}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[10%]">{t('dashboard.history.table.performance') || 'Rend.'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center w-[8%]">{t('dashboard.history.table.origin') || 'Origen'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center w-[10%]">{t('dashboard.history.table.status') || 'Estado'}</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[25%]">{t('dashboard.history.table.error_detail') || 'Detalle del Error'}</th>

                {/* <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-[33%]">{t('dashboard.history.table.error_detail') || 'Detalle del Error'}</th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentLogs.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-slate-500 font-medium bg-slate-50/50">{t('dashboard.history.no_executions') || 'No se encontraron ejecuciones.'}</td></tr>
              ) : (
                currentLogs.map((log) => {
                  const logTotal = log.success + log.errors;
                  const logRate = logTotal > 0 ? ((log.success / logTotal) * 100).toFixed(1) : 0;
                  const logEff = log.timeSec > 0 ? (logTotal / log.timeSec) : 0;

                  return (
                    <tr key={log.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold text-slate-700">{t('dashboard.history.id') || 'ID:'} {log.id}</span>
                          <span className="text-[10px] text-slate-500 mt-0.5 flex items-center">{log.runMode === 'manual' ? <><Activity className="w-3 h-3 mr-1 text-indigo-500" /> {t('dashboard.history.manual') || 'Manual'}</> : <><Clock className="w-3 h-3 mr-1 text-emerald-500" /> {t('dashboard.history.auto') || 'Auto'}</>}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-[10px] whitespace-nowrap">
                          <span className="font-bold text-slate-600 block">
                            {t('dashboard.history.start_short') || 'I:'} {formatUnixDate(log.dateStartUnixMicro ? log.dateStartUnixMicro / 1000 : log.createdAt * 1000)}
                          </span>
                          <span className="text-slate-500 block mt-0.5">
                            {t('dashboard.history.end_short') || 'F:'} {formatUnixDate(log.dateEndUnixMicro ? log.dateEndUnixMicro / 1000 : (log.finishedAt ? log.finishedAt * 1000 : log.createdAt * 1000))}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center space-x-2 text-[11px]">
                          <div>
                            <div className="flex items-center text-emerald-600 font-bold mb-0.5"><CheckCircle2 className="w-3 h-3 mr-1" /> {formatCompact(log.success)}</div>
                            {log.errors > 0 && <div className="flex items-center text-red-500 font-bold"><XCircle className="w-3 h-3 mr-1" /> {formatCompact(log.errors)}</div>}
                          </div>
                          <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1 py-0.5 rounded">({logRate}%)</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const info = getSyncCheckInfo(log);
                          if (!info) return <span className="text-[9px] text-slate-300">—</span>;

                          const icons = {
                            match: <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />,
                            warn: <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />,
                            error: <XCircle className="w-3.5 h-3.5 text-red-500" />,
                            info: <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />,
                          };

                          const bgColors = {
                            match: 'bg-emerald-50 border-emerald-200',
                            warn: 'bg-amber-50 border-amber-200',
                            error: 'bg-red-50 border-red-200',
                            info: 'bg-slate-50 border-slate-200',
                          };

                          return (
                            <div
                              className={`inline-flex flex-col items-center gap-0.5 px-1.5 py-1 rounded border text-[9px] font-bold cursor-help ${bgColors[info.icon]}`}
                              title={info.label + (info.diff != null ? ` (${info.diff.toLocaleString('en-US')} filas, ${info.pct}%)` : '')}
                            >
                              <div className="flex items-center gap-1">
                                {icons[info.icon]}
                                <span className={`text-${info.color}-700`}>
                                  {info.tgt != null ? formatCompact(info.tgt) : '—'}
                                </span>
                              </div>
                              {info.src != null && (
                                <span className="text-[8px] text-slate-500 font-normal">
                                  / {formatCompact(info.src)}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* 📏 S4 Data Rules: columna de calidad */}
                      <td className="px-3 py-2 text-center">
                        {(() => {
                          const qs = log.qualitySummary;
                          if (!qs || (!qs.totalWarned && !qs.totalBlocked)) {
                            return <span className="text-[9px] text-slate-300">—</span>;
                          }
                          const details = [];
                          if (qs.byField) {
                            Object.entries(qs.byField).forEach(([field, rules]) => {
                              Object.entries(rules).forEach(([ruleType, counts]) => {
                                const ruleName = t(`data_rules.rule_${ruleType}`);
                                if (counts.blocked > 0) details.push(`❌ ${field}: ${counts.blocked} ${ruleName}`);
                                if (counts.warned > 0) details.push(`⚠️ ${field}: ${counts.warned} ${ruleName}`);
                              });
                            });
                          }
                          const tooltipText = details.join('\n');

                          if (qs.totalBlocked > 0) {
                            return (
                              <div
                                className="inline-flex flex-col items-center gap-0.5 px-1.5 py-1 rounded border bg-red-50 border-red-200 text-[9px] font-bold cursor-help"
                                title={tooltipText}
                              >
                                <span className="text-red-600">❌ {qs.totalBlocked}</span>
                                {qs.totalWarned > 0 && (
                                  <span className="text-[8px] text-amber-600 font-normal">⚠️ {qs.totalWarned}</span>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div
                              className="inline-flex items-center gap-0.5 px-1.5 py-1 rounded border bg-amber-50 border-amber-200 text-[9px] font-bold cursor-help"
                              title={tooltipText}
                            >
                              <span className="text-amber-600">⚠️ {qs.totalWarned}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col space-y-0.5 text-[10px]"><span className="font-bold text-slate-700">{formatTime(log.timeSec, 4)}</span><span className="font-bold text-blue-600">{logEff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} r/s</span></div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {log.runMode === 'scheduled' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-bold">
                            <Clock className="w-3 h-3" /> {t('dashboard.history.exec_scheduled') || 'Programado'}
                          </span>
                        )}
                        {log.runMode === 'api' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[9px] font-bold">
                            <Code2 className="w-3 h-3" /> {t('dashboard.history.exec_api') || 'API'}
                          </span>
                        )}
                        {log.runMode === 'forced' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[9px] font-bold">
                            <Zap className="w-3 h-3" /> {t('dashboard.history.exec_forced') || 'Forzado'}
                          </span>
                        )}
                        {log.runMode === 'manual' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[9px] font-bold">
                            <Activity className="w-3 h-3" /> {t('dashboard.history.exec_manual') || 'Manual'}
                          </span>
                        )}
                        {!log.runMode && (
                          <span className="text-[9px] text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center align-middle">
                        <div className="flex flex-col items-center justify-center space-y-1.5">
                          {log.status === 1 ? <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold">{t('dashboard.history.status_completed') || 'Completado'}</span> : log.status === 0 ? <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-bold">{t('dashboard.history.status_failed') || 'Fallido'}</span> : <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-bold">{t('dashboard.history.status_partial') || 'Parcial'}</span>}

                          {log.fileUuid && (
                            (() => {
                              const isExpired = log.fileExpiresAt && (log.fileExpiresAt * 1000) < Date.now();
                              return isExpired ? (
                                <span className="text-[9px] text-slate-400 flex items-center bg-slate-50 px-1.5 py-0.5 rounded cursor-not-allowed" title={t('dashboard.history.expired_desc') || 'El archivo fue eliminado por seguridad'}>
                                  <Clock className="w-3 h-3 mr-0.5" /> {t('dashboard.history.expired') || 'Expirado'}
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleDownload(log.fileUuid)}
                                  disabled={downloadingId === log.fileUuid}
                                  className="text-[9px] text-blue-600 hover:text-blue-800 flex items-center font-bold bg-blue-50 hover:bg-blue-100 px-1.5 py-1 rounded border border-blue-200 transition-colors shadow-sm disabled:opacity-50"
                                  title={t('dashboard.history.download_tooltip') || 'Descargar archivo exportado'}
                                >
                                  {downloadingId === log.fileUuid ? (
                                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Download className="w-3 h-3 mr-1" />
                                  )}
                                  {downloadingId === log.fileUuid ? (t('dashboard.history.downloading') || 'Procesando...') : (t('dashboard.history.download') || 'Descargar')}
                                </button>
                              );
                            })()
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle">
                        {log.status === 1 ? (
                          <span className="text-[11px] text-emerald-600/70 font-medium italic">{t('dashboard.history.no_errors') || 'Sin errores'}</span>
                        ) : (
                          <div className={`text-[11px] p-2 rounded border break-words whitespace-normal select-text font-mono leading-relaxed max-h-[100px] overflow-y-auto ${log.status === 2
                            ? 'text-amber-800 bg-amber-50/70 border-amber-200'
                            : 'text-red-700 bg-red-50/60 border-red-100'
                            }`}>
                            {log.errorMessage
                              ? formatErrorMessage(log.errorMessage, t)
                              : (t('dashboard.history.unknown_error') || 'Error crítico desconocido.')}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between mt-auto">
            <p className="text-[11px] text-slate-500 font-medium">{t('dashboard.history.showing') || 'Mostrando'} <span className="font-bold">{((currentPage - 1) * itemsPerPage) + 1}</span> - <span className="font-bold">{Math.min(currentPage * itemsPerPage, processedLogs.length)}</span></p>
            <div className="flex space-x-1.5">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="p-1 rounded bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="p-1 rounded bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        showCancel={modalState.showCancel}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        onAccept={modalState.onAccept}
      />
    </div>
  );
};